import React, { useState } from 'react';
import {Box,Flex,Text,Input,IconButton,VStack,HStack,Avatar, Icon, useMediaQuery, Image} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, CloseIcon } from '@chakra-ui/icons';
import { useNavigate} from 'react-router-dom';
import { FaSignOutAlt } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../services/API';

// Component for individual chat history items in sidebar
export const ChatHistoryItem = ({ chat, isSelected, onEdit, onDelete, onClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    const title = chat.title || 'Untitled Chat';
    const firstLetter = title.charAt(0).toUpperCase();

    return (
        <Flex
            py={2}
            px={3}
            alignItems="center"
            cursor="pointer"
            _hover={{ bg: '#e2e8f0' }}
            borderRadius="md"
            bg={isSelected ? '#e2e8f0' : 'transparent'}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            justify="space-between"
            onClick={() => onClick(chat)}
        >
            <Flex alignItems="center" flex={1}>
                <Avatar
                    size="xs"
                    name={firstLetter}
                    bg="#e2e8f0"
                    color="#1f2937"
                    mr={3}
                />
                <Text color="#334155" fontSize="sm" noOfLines={1}>
                    {title}
                </Text>
            </Flex>
            {(isHovered || isSelected) && (
                <HStack spacing={2} ml={2}>
                    <IconButton
                        icon={<EditIcon />}
                        aria-label="Edit chat title"
                        size="xs"
                        variant="ghost"
                        color="#1f2937"
                        _hover={{ bg: 'blackAlpha.100' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit(chat);
                        }}
                    />
                    <IconButton
                        icon={<DeleteIcon />}
                        aria-label="Delete chat"
                        size="xs"
                        variant="ghost"
                        color="#1f2937"
                        _hover={{ bg: 'blackAlpha.100' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(chat);
                        }}
                    />
                </HStack>
            )}
        </Flex>
    );
};

// Component for the chat list sidebar
export const ChatListSidebar = ({
    chatHistory = { today: [], yesterday: [], last_seven_days: [], older: [] },
    onNewChat,
    selectedChatId,
    onEditChat,
    onDeleteChat,
    onSelectChat,
    searchQuery,
    onSearchChange,
    isCollapsed = false,
  }) => {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [isMobile] = useMediaQuery("(max-width: 610px)");
    
    const handleLogout = async () => {
      try {
        const response = await logout();
        if (response.success) {
          setUser(null);
          Object.keys(sessionStorage)
            .filter((key) => key.startsWith('msal'))
            .forEach((key) => sessionStorage.removeItem(key));
          navigate('/login');
        }
      } catch (error) {
        console.error('Logout failed:', error);
      }
    };
  
    const categoryTitles = {
      today: 'Today',
      yesterday: 'Yesterday',
      last_seven_days: 'Previous Week',
      older: 'Older',
    };
    
    if (isMobile && isCollapsed) {
      return null;
    }
  
    return (
      <Flex 
        direction="column" 
        w={isMobile ? (isCollapsed ? "0" : "100%") : (isCollapsed ? "0" : "300px")}
        minW={isMobile ? (isCollapsed ? "0" : "100%") : (isCollapsed ? "0" : "300px")}
        bg="#ffffff" 
        h="100vh"
        position={isMobile ? "fixed" : "relative"}
        zIndex={isMobile ? "1000" : "1"}
        left={0}
        top={0}
        transition="all 0.3s ease"
        overflow="hidden"
        borderRight="1px"
        borderColor="#3498db"
      >
        <Flex
          px={0}
          py={3}
          minH="60px"
          borderBottom="1px"
          borderColor="#3498db"
          justifyContent="center"
          alignItems="center"
        >
          <Image
            src="/American-Standard-Circuits_Logo_Full-Color (1).png"
            alt="MountainGPT Logo"
            height="32px"
            width="auto"
            maxW="180px"
            objectFit="contain"
            objectPosition="center"
            display="block"
            mx="auto"
          />
        </Flex>
  
        {/* Commenting new chat button for now */}
        {/* <Box px={2} py={2}>
          <Flex
            bg="white"
            color="black"
            p={2}
            borderRadius="md"
            cursor="pointer"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: '#F5F5F5' }}
            onClick={onNewChat}
          >
            <AddIcon boxSize="12px" mr={2} />
            <Text>New Chat</Text>
          </Flex>
        </Box> */}
  
        <Box px={2} py={2}>
          <Flex
            position="relative"
            align="center"
            bg="#f1f5f9"
            borderRadius="md"
            border="1px solid"
            borderColor="#e2e8f0"
            _hover={{ borderColor: '#cbd5e1' }}
          >
            {/* Search Icon */}
            <Box position="absolute" left="3" color="#64748b">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 14L11 11"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
  
            {/* Search Input */}
            <Input
              placeholder="Search chats..."
              size="sm"
              bg="transparent"
              border="none"
              color="#1f2937"
              value={searchQuery}
              onChange={onSearchChange}
              pl="10"
              pr="8"
              py="2"
              _placeholder={{ color: '#64748b' }}
              _hover={{ bg: 'transparent' }}
              _focus={{
                border: 'none',
                boxShadow: 'none',
                bg: 'transparent',
              }}
            />
  
            {/* Clear Button */}
            {searchQuery && (
              <IconButton
                icon={<CloseIcon boxSize="8px" />}
                size="sm"
                position="absolute"
                right="2"
                variant="ghost"
                color="#64748b"
                _hover={{ color: '#1f2937', bg: 'transparent' }}
                onClick={() => onSearchChange({ target: { value: '' } })}
                aria-label="Clear search"
                minW="24px"
                h="24px"
                zIndex={2}
              />
            )}
          </Flex>
        </Box>
  
        <VStack
          align="stretch"
          spacing={1}
          px={2}
          flex={1}
          overflowY="auto"
          css={{
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f5f9',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#cbd5e1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#94a3b8',
            },
          }}
        >
          {Object.entries(chatHistory).map(([category, chats]) => (
            chats.length > 0 && (
              <Box key={category} mb={4}>
                <Text fontSize="sm" fontWeight="bold" color="#64748b" mb={2}>
                  {categoryTitles[category]}
                </Text>
                {chats.map((chat) => (
                  <ChatHistoryItem
                    key={chat.id}
                    chat={chat}
                    isSelected={chat.id === selectedChatId}
                    onEdit={onEditChat}
                    onDelete={onDeleteChat}
                    onClick={onSelectChat}
                  />
                ))}
              </Box>
            )
          ))}
          {Object.values(chatHistory).flat().length === 0 && searchQuery && (
            <Text color="#64748b" fontSize="sm" textAlign="center" py={4}>
              No chats found
            </Text>
          )}
        </VStack>
  
        {/* Logout Button */}
        <Box px={2} py={2} borderTop="1px" borderColor="#e2e8f0">
          <Flex
            bg="#f1f5f9"
            color="#1f2937"
            p={2}
            borderRadius="md"
            cursor="pointer"
            alignItems="center"
            justifyContent="center"
            _hover={{ bg: '#e2e8f0' }}
            onClick={handleLogout}
          >
            <Icon as={FaSignOutAlt} boxSize="12px" mr={2} />
            <Text>Logout</Text>
          </Flex>
        </Box>
      </Flex>
    );
  };
  
